import {
	App,
	createAssetShapeAtPoint,
	createBookmarkShapeAtPoint,
	createEmbedShapeAtPoint,
	createShapeId,
	createShapesFromFiles,
	FONT_FAMILIES,
	FONT_SIZES,
	getEmbedInfo,
	getValidHttpURLList,
	isSvgText,
	isValidHttpURL,
	TEXT_PROPS,
	TLArrowShapeDef,
	TLBookmarkShapeDef,
	TLClipboardModel,
	TLEmbedShapeDef,
	TLGeoShapeDef,
	TLTextShapeDef,
	useApp,
} from '@tldraw/editor'
import { VecLike } from '@tldraw/primitives'
import { isNonNull } from '@tldraw/utils'
import { compressToBase64, decompressFromBase64 } from 'lz-string'
import { useCallback, useEffect } from 'react'

/** @public */
export type EmbedInfo = {
	width: number
	height: number
	doesResize: boolean
	isEmbedUrl: (url: string) => boolean
	toEmbed: (url: string) => string
}

async function blobAsString(blob: Blob) {
	return new Promise<string>((resolve, reject) => {
		const reader = new FileReader()
		reader.addEventListener('loadend', () => {
			const text = reader.result
			resolve(text as string)
		})
		reader.addEventListener('error', () => {
			reject(reader.error)
		})
		reader.readAsText(blob)
	})
}

async function dataTransferItemAsString(item: DataTransferItem) {
	return new Promise<string>((resolve) => {
		item.getAsString((text) => {
			resolve(text)
		})
	})
}

const INPUTS = ['input', 'select', 'textarea']

function disallowClipboardEvents(app: App) {
	const { activeElement } = document
	return (
		app.isMenuOpen ||
		(activeElement &&
			(activeElement.getAttribute('contenteditable') ||
				INPUTS.indexOf(activeElement.tagName.toLowerCase()) > -1))
	)
}

function stripHtml(html: string) {
	// See <https://github.com/developit/preact-markup/blob/4788b8d61b4e24f83688710746ee36e7464f7bbc/src/parse-markup.js#L60-L69>
	const doc = document.implementation.createHTMLDocument('')
	doc.documentElement.innerHTML = html
	return doc.body.textContent || doc.body.innerText || ''
}

// Clear the clipboard when the user copies nothing
const clearPersistedClipboard = () => {
	window.navigator.clipboard.writeText('')
}

/**
 * Write serialized data to the local storage.
 *
 * @internal
 * @param data - The string to write.
 * @param kind - The kind of data to write.
 */
const persistClipboard = (data: any, kind: 'text' | 'file' | 'content') => {
	const s = compressToBase64(
		JSON.stringify({
			type: 'application/tldraw',
			kind,
			data,
		})
	)

	return s
}

/**
 * When the clipboard has tldraw content, paste it into the scene.
 *
 * @internal
 * @param clipboard - The clipboard model.
 * @param point - The center point at which to paste the content.
 */
const pasteTldrawContent = async (app: App, clipboard: TLClipboardModel, point?: VecLike) => {
	const p = point ?? (app.inputs.shiftKey ? app.inputs.currentPagePoint : undefined)

	app.mark('paste')
	app.putContent(clipboard, {
		point: p,
		select: true,
	})
}

/**
 * When the clipboard has plain text, create a text shape and insert it into the scene
 *
 * @internal
 * @param text - The text to paste.
 * @param point - The point at which to paste the text.
 */
const pastePlainText = async (app: App, text: string, point?: VecLike) => {
	const p = point ?? (app.inputs.shiftKey ? app.inputs.currentPagePoint : app.pageCenter)
	const defaultProps = app.getShapeUtilByDef(TLTextShapeDef).defaultProps()

	// Measure the text with default values
	const { w, h } = app.textMeasure.measureText({
		...TEXT_PROPS,
		text: stripHtml(text),
		fontFamily: FONT_FAMILIES[defaultProps.font],
		fontSize: FONT_SIZES[defaultProps.size],
		width: 'fit-content',
	})

	app.mark('paste')
	app.createShapes([
		{
			id: createShapeId(),
			type: 'text',
			x: p.x - w / 2,
			y: p.y - h / 2,
			props: {
				text: stripHtml(text),
				autoSize: true,
			},
		},
	])
}

/**
 * When the clipboard has plain text that is a valid URL, create a bookmark shape and insert it
 * into the scene
 *
 * @internal
 * @param url - The URL to paste.
 * @param point - The point at which to paste the file.
 */
const pasteUrl = async (app: App, url: string, point?: VecLike) => {
	const p = point ?? (app.inputs.shiftKey ? app.inputs.currentPagePoint : app.pageCenter)

	// Lets see if its an image and we have CORs
	try {
		const resp = await fetch(url)
		if (resp.headers.get('content-type')?.match(/^image\//)) {
			app.mark('paste')
			pasteFiles(app, [url])
			return
		}
	} catch (err: any) {
		if (err.message !== 'Failed to fetch') {
			console.error(err)
		}
	}

	const embedInfo = getEmbedInfo(url)
	if (embedInfo) {
		app.mark('paste')
		await createEmbedShapeAtPoint(app, embedInfo.url, p, embedInfo.definition)
	} else {
		app.mark('paste')
		await createBookmarkShapeAtPoint(app, url, p)
	}
}

const pasteSvgText = (app: App, text: string, point?: VecLike) => {
	const p = point ?? (app.inputs.shiftKey ? app.inputs.currentPagePoint : app.pageCenter)

	app.mark('paste')
	createAssetShapeAtPoint(app, text, p)
}

/**
 * When the clipboard has a file, create an image shape from the file and paste it into the scene
 *
 * @internal
 * @param url - The file's url.
 * @param point - The point at which to paste the file.
 */
const pasteFiles = async (app: App, urls: string[], point?: VecLike) => {
	const p = point ?? (app.inputs.shiftKey ? app.inputs.currentPagePoint : app.pageCenter)

	const blobs = await Promise.all(urls.map(async (url) => await (await fetch(url)).blob()))

	const files = blobs.map(
		(blob) =>
			new File([blob], 'TLdrawFile', {
				type: blob.type,
			})
	)

	app.mark('paste')
	await createShapesFromFiles(app, files, p, false)

	urls.forEach((url) => URL.revokeObjectURL(url))
}

/**
 * When the user copies, write the contents to local storage and to the clipboard
 *
 * @param app - App
 * @public
 */
const handleMenuCopy = (app: App) => {
	const content = app.getContent()
	if (!content) {
		clearPersistedClipboard()
		return
	}

	const stringifiedPayload = persistClipboard(content, 'content')

	if (typeof window?.navigator !== 'undefined') {
		// Extract the text from the clipboard
		const textItems = content.shapes
			.map((shape) => {
				if (TLTextShapeDef.is(shape) || TLGeoShapeDef.is(shape) || TLArrowShapeDef.is(shape)) {
					return shape.props.text
				}
				if (TLBookmarkShapeDef.is(shape) || TLEmbedShapeDef.is(shape)) {
					return shape.props.url
				}
				return null
			})
			.filter(isNonNull)

		if (navigator.clipboard?.write) {
			const htmlBlob = new Blob([`<tldraw>${stringifiedPayload}</tldraw>`], {
				type: 'text/html',
			})

			let textContent = textItems.join(' ')

			// This is a bug in chrome android where it won't paste content if
			// the text/plain content is "" so we need to always add an empty
			// space 🤬
			if (textContent === '') {
				textContent = ' '
			}

			navigator.clipboard.write([
				new ClipboardItem({
					'text/html': htmlBlob,
					'text/plain': new Blob([textContent], { type: 'text/plain' }),
				}),
			])
		} else if (navigator.clipboard.writeText) {
			navigator.clipboard.writeText(`<tldraw>${stringifiedPayload}</tldraw>`)
		}
	}
}

const pasteText = (app: App, data: string, point?: VecLike) => {
	const validUrlList = getValidHttpURLList(data)
	if (validUrlList) {
		for (const url of validUrlList) {
			pasteUrl(app, url, point)
		}
	} else if (isValidHttpURL(data)) {
		pasteUrl(app, data, point)
	} else if (isSvgText(data)) {
		pasteSvgText(app, data, point)
	} else {
		pastePlainText(app, data, point)
	}
}

const handleFilesBlob = async (app: App, blobs: Blob[], point?: VecLike) => {
	const urls = blobs.map((blob) => URL.createObjectURL(blob))

	pasteFiles(app, urls, point)
}

const handleHtmlString = async (app: App, html: string, point?: VecLike) => {
	const s = html.match(/<tldraw[^>]*>(.*)<\/tldraw>/)?.[1]
	if (s) {
		try {
			const json = JSON.parse(decompressFromBase64(s)!)
			if (json.type === 'application/tldraw') {
				pasteTldrawContent(app, json.data, point)
			} else {
				pasteText(app, s, point)
			}
		} catch (error) {
			pasteText(app, s, point)
		}
	} else {
		const rootNode = new DOMParser().parseFromString(html, 'text/html')
		const bodyNode = rootNode.querySelector('body')

		// Edge on Windows 11 home appears to paste a link as a single <a/> in
		// the HTML document. If we're pasting a single like tag we'll just
		// assume the user meant to paste the URL.
		const isHtmlSingleLink =
			bodyNode &&
			Array.from(bodyNode.children).filter((el) => el.nodeType === 1).length === 1 &&
			bodyNode.firstElementChild &&
			bodyNode.firstElementChild.tagName === 'A' &&
			bodyNode.firstElementChild.hasAttribute('href') &&
			bodyNode.firstElementChild.getAttribute('href') !== ''

		if (isHtmlSingleLink) {
			const href = bodyNode.firstElementChild.getAttribute('href')!
			pasteText(app, href, point)
		} else {
			pasteText(app, html, point)
		}
	}
}

const handleTextString = async (app: App, text: string, point?: VecLike) => {
	const s = text.trim()

	const tldrawContent = text.match(/<tldraw[^>]*>(.*)<\/tldraw>/)?.[1]
	if (tldrawContent) {
		handleHtmlString(app, text)
	} else if (s) {
		try {
			const json = JSON.parse(s)
			if (json.type === 'application/tldraw') {
				pasteTldrawContent(app, json.data, point)
			} else {
				pasteText(app, s, point)
			}
		} catch (error) {
			pasteText(app, s, point)
		}
	}
}

const handleNativeDataTransferPaste = async (
	app: App,
	clipboardData: DataTransfer,
	point?: VecLike
) => {
	// Do not paste while in any editing state
	if (app.isIn('select.editing')) return

	if (clipboardData) {
		const items = Object.values(clipboardData.items)

		// In some cases, the clipboard will contain both the name of a file and the file itself
		// we need to avoid writing a text shape for the name AND an image or video shape for the file
		const writingFile = items.some((item) => item.kind === 'file')

		// If we're pasting in tldraw content (shapes, etc) then the clipboard may
		// contain both text content. We'll only paste the content.
		const writingContent = items.some((item) => item.type === 'text/html')

		// We need to handle files separately because if we want them to
		// be placed next to each other, we need to create them all at once

		const files: Blob[] = []
		const text: DataTransferItem[] = []

		items.forEach((item) => {
			if (item.kind === 'file') {
				const file = item.getAsFile()
				if (file) {
					files.push(file)
				}
			} else if (item.kind === 'string') {
				text.push(item)
			}
		})

		if (files.length > 0) {
			handleFilesBlob(app, files, point)
		}

		for (const item of text) {
			if (!writingFile && item.type === 'text/html') {
				await handleHtmlString(app, await dataTransferItemAsString(item), point)
			} else if (item.type === 'text/plain') {
				if (!writingContent) {
					await handleTextString(app, await dataTransferItemAsString(item), point)
				}
			}
		}
	}
}

const handleNativeClipboardPaste = async (
	app: App,
	clipboardItems: ClipboardItem[],
	point?: VecLike
) => {
	// Do not paste while in any editing state
	if (app.isIn('select.editing')) return

	const isFile = (item: ClipboardItem) => {
		return item.types.find((i) => i.match(/^image\//))
	}

	// In some cases, the clipboard will contain both the name of a file and the file itself
	// we need to avoid writing a text shape for the name AND an image or video shape for the file
	const writingFile = clipboardItems.some((item) => isFile(item))

	// If we're pasting in tldraw content (shapes, etc) then the clipboard may
	// contain both text content. We'll only paste the content.
	const writingContent = clipboardItems.some((item) => item.types.includes('text/html'))

	// We need to handle files separately because if we want them to
	// be placed next to each other, we need to create them all at once

	const files: ClipboardItem[] = clipboardItems.filter((item) => {
		if (item.types.find((i) => i.match(/^image\//))) {
			return true
		}

		return false
	})

	await Promise.all(
		files.map(async (item) => {
			const type = item.types.find((t) => t !== 'text/plain' && t !== 'text/html')
			if (type) {
				const file = await item.getType(type)
				if (file) {
					await handleFilesBlob(app, [file], point)
				}
			}
		})
	)

	for (const item of clipboardItems) {
		if (item.types.includes('text/html')) {
			if (writingFile) break

			const blob = await item.getType('text/html')
			await handleHtmlString(app, await blobAsString(blob), point)
		} else if (item.types.includes('text/uri-list')) {
			if (writingContent) break

			const blob = await item.getType('text/uri-list')
			await pasteUrl(app, await blobAsString(blob), point)
		} else if (item.types.includes('text/plain')) {
			if (writingContent) break

			const blob = await item.getType('text/plain')
			await handleTextString(app, await blobAsString(blob), point)
		}
	}
}

/** @public */
export function useMenuClipboardEvents() {
	const app = useApp()

	const copy = useCallback(
		function onCopy() {
			if (app.selectedIds.length === 0) return
			handleMenuCopy(app)
		},
		[app]
	)

	const cut = useCallback(
		function onCut() {
			if (app.selectedIds.length === 0) return
			handleMenuCopy(app)
			app.deleteShapes()
		},
		[app]
	)

	const paste = useCallback(
		async function onPaste(data: DataTransfer | ClipboardItem[], point?: VecLike) {
			if (Array.isArray(data) && data[0] instanceof ClipboardItem) {
				handleNativeClipboardPaste(app, data, point)
			} else {
				navigator.clipboard.read().then((clipboardItems) => {
					paste(clipboardItems, app.inputs.currentPagePoint)
				})
			}

			// else {
			// 	handleScenePaste(app, point)
			// }
		},
		[app]
	)

	return {
		copy,
		cut,
		paste,
	}
}

/** @public */
export function useNativeClipboardEvents() {
	const app = useApp()

	useEffect(() => {
		const copy = () => {
			if (app.selectedIds.length === 0 || app.editingId !== null || disallowClipboardEvents(app))
				return
			handleMenuCopy(app)
		}

		function cut() {
			if (app.selectedIds.length === 0 || app.editingId !== null || disallowClipboardEvents(app))
				return
			handleMenuCopy(app)
			app.deleteShapes()
		}

		const paste = (event: ClipboardEvent) => {
			if (app.editingId !== null || disallowClipboardEvents(app)) return
			if (event.clipboardData && !app.inputs.shiftKey) {
				handleNativeDataTransferPaste(app, event.clipboardData)
			} else {
				navigator.clipboard.read().then((clipboardItems) => {
					if (Array.isArray(clipboardItems) && clipboardItems[0] instanceof ClipboardItem) {
						handleNativeClipboardPaste(app, clipboardItems, app.inputs.currentPagePoint)
					}
				})
			}
		}

		document.addEventListener('copy', copy)
		document.addEventListener('cut', cut)
		document.addEventListener('paste', paste)

		return () => {
			document.removeEventListener('copy', copy)
			document.removeEventListener('cut', cut)
			document.removeEventListener('paste', paste)
		}
	}, [app])
}
